import type {
  CannedChargeType,
  CannedWorkTaskChargePublic,
  ChargeType,
  Currency,
  QuoteRevisionTaskChargePublic,
  QuoteTaskChargePublic,
} from "@common/client"
import { formatCurrency } from "@common/lib/currency"
import { toNumberOrNull } from "@common/lib/number"

export type Charge =
  | QuoteTaskChargePublic
  | QuoteRevisionTaskChargePublic
  | CannedWorkTaskChargePublic

export type PricedCharge = Pick<
  Charge,
  "type" | "quantity" | "sale_unit_price" | "tax_amount" | "currency"
>

export type ResolvedCurrency = Currency | "mixed" | "none"

export const CANNED_CHARGE_TYPES = [
  "part",
  "labour",
  "labour_flat_rate",
  "misc",
  "outside_service",
] as const satisfies readonly CannedChargeType[]

export const AMOUNT_ONLY_TYPES = [
  "misc",
  "part_flat_rate",
  "outside_service",
  "estimate",
  "freight",
  "insurance",
] as const satisfies readonly ChargeType[]

export function isAmountOnly(type: ChargeType): boolean {
  return (AMOUNT_ONLY_TYPES as readonly ChargeType[]).includes(type)
}

export function isHourly(type: string): boolean {
  return type === "labour" || type === "labour_flat_rate"
}

export const FLAT_TOTAL_UNIT = "job"

export function isFlatTotal(
  charge: Pick<Charge, "type" | "unit_of_measure">,
): boolean {
  return (
    charge.type === "labour_flat_rate" &&
    charge.unit_of_measure === FLAT_TOTAL_UNIT
  )
}

export function chargeQuantity(charge: PricedCharge): number | null {
  const quantity = toNumberOrNull(charge.quantity)
  if (quantity !== null) return quantity
  return isAmountOnly(charge.type) ? 1 : null
}

export function chargeAmount(charge: PricedCharge): number | null {
  const quantity = chargeQuantity(charge)
  const price = toNumberOrNull(charge.sale_unit_price)
  return quantity !== null && price !== null ? quantity * price : null
}

export function chargeCost(charge: Charge): number | null {
  const quantity = chargeQuantity(charge)
  const purchase = toNumberOrNull(charge.purchase_unit_price)
  return quantity !== null && purchase !== null ? quantity * purchase : null
}

export function marginOf(purchase: number, sale: number): number | null {
  return purchase > 0 ? ((sale - purchase) / purchase) * 100 : null
}

export function saleFor(purchase: number, margin: number): number | null {
  return margin >= -100 ? purchase * (1 + margin / 100) : null
}

const SIGNED_DECIMAL = /^-?(\d+\.?\d*|\.\d+)$/

export function parsePercent(raw: string): number | null {
  const trimmed = raw.trim()
  return SIGNED_DECIMAL.test(trimmed) ? Number.parseFloat(trimmed) : null
}

export function chargeMargin(charge: Charge): number | null {
  const purchase = toNumberOrNull(charge.purchase_unit_price)
  const sale = toNumberOrNull(charge.sale_unit_price)
  if (purchase === null || sale === null) return null
  return marginOf(purchase, sale)
}

export function chargeTax(charge: PricedCharge): number {
  return toNumberOrNull(charge.tax_amount) ?? 0
}

export function sumAmounts(charges: PricedCharge[]): number | null {
  let total = 0
  for (const charge of charges) {
    const amount = chargeAmount(charge)
    if (amount === null) return null
    total += amount
  }
  return total
}

export function sumPriced(charges: PricedCharge[]): number {
  return charges.reduce(
    (total, charge) => total + (chargeAmount(charge) ?? 0),
    0,
  )
}

export function formatPrice(
  charges: PricedCharge[],
  fallback: Currency,
): string {
  const totals = new Map<Currency, number>()
  for (const charge of charges) {
    if (charge.currency === null) continue
    const amount = chargeAmount(charge) ?? 0
    totals.set(charge.currency, (totals.get(charge.currency) ?? 0) + amount)
  }
  if (totals.size === 0) return formatCurrency(0, fallback)
  return [...totals]
    .map(([currency, amount]) => formatCurrency(amount, currency))
    .join(" · ")
}

export function sumCosts(charges: Charge[]): number | null {
  const costs = charges.flatMap((charge) => chargeCost(charge) ?? [])
  return costs.length ? costs.reduce((total, cost) => total + cost, 0) : null
}

export function resolveCurrency(charges: PricedCharge[]): ResolvedCurrency {
  const currencies = new Set<Currency>()
  for (const charge of charges) {
    if (charge.currency) currencies.add(charge.currency)
  }
  if (currencies.size === 0) return "none"
  if (currencies.size > 1) return "mixed"
  const [only] = currencies
  return only
}

export function formatMoney(
  amount: number | null,
  currency: ResolvedCurrency,
): string {
  if (amount === null || currency === "mixed" || currency === "none") {
    return ""
  }
  return formatCurrency(amount, currency)
}

function sumByCurrency(charges: PricedCharge[]): Map<Currency, number> | null {
  const totals = new Map<Currency, number>()
  for (const charge of charges) {
    const amount = chargeAmount(charge)
    if (amount === null || charge.currency === null) return null
    totals.set(charge.currency, (totals.get(charge.currency) ?? 0) + amount)
  }
  return totals
}

export function formatTotals(charges: PricedCharge[]): string[] {
  const currency = resolveCurrency(charges)
  if (currency !== "mixed") return [formatMoney(sumAmounts(charges), currency)]

  const totals = sumByCurrency(charges)
  if (totals === null) return []
  return [...totals].map(([only, amount]) => formatMoney(amount, only))
}

export function chargeTypeLabel(type: ChargeType): string {
  switch (type) {
    case "misc":
      return "Miscellaneous"
    case "freight":
      return "Freight"
    case "insurance":
      return "Insurance"
    case "part_flat_rate":
      return "Parts flat rate"
    case "labour":
      return "Labor"
    case "labour_flat_rate":
      return "Flat rate"
    case "part":
      return "Part"
    case "outside_service":
      return "Outside service"
    case "estimate":
      return "Estimate"
  }
}
